import http from './api'
import config from './config'
import { User } from '../types/user'

export interface SignInData {
  email: string
  password: string
}

export interface SignUpData {
  name: string
  email: string
  password: string
}

export interface AuthResponse {
  user: User
  token: string
}

class AuthService {
  async signIn(data: SignInData): Promise<AuthResponse> {
    const response = await http.post('/auth/sign-in', data)
    return response.data
  }

  async signUp(data: SignUpData): Promise<AuthResponse> {
    const response = await http.post('/auth/sign-up', data)
    return response.data
  }

  async getCurrentUser(): Promise<User> {
    const response = await http.get('/auth/current-user')
    return response.data
  }

  async forgotPassword(email: string): Promise<{ message: string; resetLink?: string }> {
    const frontendUrl = typeof window !== 'undefined' ? window.location.origin : ''
    const response = await http.post<{ message: string; resetLink?: string }>(
      '/auth/forgot-password',
      { email, ...(frontendUrl && { frontendUrl }) },
      { timeout: 90000 }
    )
    return response.data
  }

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await http.post<{ message: string }>('/auth/reset-password', {
      token,
      password,
    })
    return response.data
  }

  getGoogleAuthUrl(): string {
    return `${config.baseURL}/api/auth/google`
  }

  signInWithGoogle(): void {
    window.location.href = this.getGoogleAuthUrl()
  }

  logout(): void {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('authData')
  }

  isTokenValid(): boolean {
    const authDataStr = localStorage.getItem('authData')
    if (!authDataStr) return false
    
    try {
      const authData = JSON.parse(authDataStr)
      const oneDayInMs = 24 * 60 * 60 * 1000 // 1 day
      const isExpired = Date.now() - authData.timestamp > oneDayInMs
      
      if (isExpired) {
        this.logout()
        return false
      }
      
      return true
    } catch {
      this.logout()
      return false
    }
  }

  restoreAuth(): { user: User | null; token: string | null } {
    if (!this.isTokenValid()) {
      return { user: null, token: null }
    }
    
    const user = this.getUser()
    const token = this.getToken()
    
    return { user, token }
  }

  getToken(): string | null {
    return localStorage.getItem('token')
  }

  getUser(): User | null {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  }

  setAuthData(user: User, token: string): void {
    const authData = {
      user,
      token,
      timestamp: Date.now()
    }
    localStorage.setItem('authData', JSON.stringify(authData))
    localStorage.setItem('user', JSON.stringify(user))
    localStorage.setItem('token', token)
  }
}

const authService = new AuthService()
export default authService
